import { Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeStorageKey = 'edge_theme_mode';
  readonly isDarkMode = signal(false);

  initializeTheme(): void {
    const savedTheme = this.safeGetStoredTheme();
    const shouldUseDark = savedTheme === 'dark';
    this.applyTheme(shouldUseDark, false);
  }

  toggleTheme(): void {
    this.applyTheme(!this.isDarkMode(), true);
  }

  setTheme(mode: ThemeMode): void {
    this.applyTheme(mode === 'dark', true);
  }

  private applyTheme(isDark: boolean, persist: boolean): void {
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('dark', isDark);
    body.classList.toggle('dark', isDark);
    this.isDarkMode.set(isDark);

    if (persist) {
      this.safeStoreTheme(isDark ? 'dark' : 'light');
    }

    this.syncBrowserThemeColor();
  }

  private syncBrowserThemeColor(): void {
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      return;
    }

    const surfaceColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-app')
      .trim();
    if (surfaceColor) {
      themeMeta.setAttribute('content', surfaceColor);
    }
  }

  private safeGetStoredTheme(): ThemeMode | null {
    try {
      const value = localStorage.getItem(this.themeStorageKey);
      return value === 'dark' || value === 'light' ? value : null;
    } catch {
      return null;
    }
  }

  private safeStoreTheme(mode: ThemeMode): void {
    try {
      localStorage.setItem(this.themeStorageKey, mode);
    } catch {
      // Ignore storage errors in private mode/restricted environments.
    }
  }
}
